/**
 * siteMapLayerVisibilityMemory — per-site, per-user layer visibility (and
 * category opacity) preferences for the Site Map tab.
 *
 * Before this, the tab's hidden-category / hidden-layer / hidden-parcel
 * toggles were plain `useState` Sets that reset on every visit. They are
 * viewer preferences, not shared site truth, so they persist in
 * localStorage (the `siteMapViewportMemory` precedent) — writing them to
 * the site doc would fire a revision-guarded `saveSiteLayersBatch` write on
 * every checkbox click and fight concurrent editors.
 *
 * `SiteLayer.visible` on the doc remains the authoring-time default;
 * this store is a per-viewer overlay on top of it.
 *
 * Storage backend: localStorage when a probe round-trip succeeds, otherwise
 * a module-level in-memory Map. The fallback keeps toggles working for the
 * session in environments where storage is blocked or broken (Safari
 * private mode throwing on setItem, enterprise lockdowns, jsdom test envs
 * without functional storage) — persistence degrades, behavior doesn't.
 */

export interface SavedSiteMapLayerVisibility {
  hiddenCategories: string[];
  hiddenLayerIds: string[];
  hiddenParcelIds: string[];
  /** Geometry kinds ('polygon' | 'line' | 'point') hidden by the viewer. */
  hiddenGeometryTypes?: string[];
  /** Lane-2 — refdata dataset ids the viewer streams as context overlays. */
  contextDatasets?: string[];
  /** P4 — per-category fill-opacity override, 0.05–1. */
  categoryOpacity?: Record<string, number>;
  /** P4 — full layer-id z-order (array order = draw order, last on top). */
  layerOrder?: string[];
}

const KEY_PREFIX = 'siterag:siteMapLayerVisibility:';
const VERSION = 1;
const PROBE_KEY = `${KEY_PREFIX}__probe__`;

/* ── Storage facade (localStorage with in-memory fallback) ────────────── */

let memoryStore: Map<string, string> | null = null;
let probed: boolean | null = null;

function localStorageUsable(): boolean {
  if (probed !== null) return probed;
  try {
    window.localStorage.setItem(PROBE_KEY, '1');
    probed = window.localStorage.getItem(PROBE_KEY) === '1';
    window.localStorage.removeItem(PROBE_KEY);
  } catch {
    probed = false;
  }
  return probed;
}

function rawGet(key: string): string | null {
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  return memoryStore?.get(key) ?? null;
}

function rawSet(key: string, value: string): void {
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      window.localStorage.setItem(key, value);
      return;
    } catch {
      /* quota — fall through to memory */
    }
  }
  (memoryStore ??= new Map()).set(key, value);
}

function rawClearPrefix(prefix: string): void {
  memoryStore?.clear();
  if (typeof window === 'undefined' || !localStorageUsable()) return;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(prefix)) window.localStorage.removeItem(k);
    }
  } catch {
    /* swallow */
  }
}

/* ── Payload (de)serialisation ────────────────────────────────────────── */

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function opacityRecord(value: unknown): Record<string, number> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0.05 && v <= 1) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function readSiteMapLayerVisibility(siteId: string): SavedSiteMapLayerVisibility | null {
  if (!siteId) return null;
  try {
    const raw = rawGet(KEY_PREFIX + siteId);
    if (!raw) return null;
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (data.v !== VERSION) return null;
    const categoryOpacity = opacityRecord(data.categoryOpacity);
    const layerOrder = stringArray(data.layerOrder);
    const hiddenGeometryTypes = stringArray(data.hiddenGeometryTypes);
    const contextDatasets = stringArray(data.contextDatasets);
    return {
      hiddenCategories: stringArray(data.hiddenCategories),
      hiddenLayerIds: stringArray(data.hiddenLayerIds),
      hiddenParcelIds: stringArray(data.hiddenParcelIds),
      ...(hiddenGeometryTypes.length > 0 ? { hiddenGeometryTypes } : {}),
      ...(contextDatasets.length > 0 ? { contextDatasets } : {}),
      ...(categoryOpacity ? { categoryOpacity } : {}),
      ...(layerOrder.length > 0 ? { layerOrder } : {}),
    };
  } catch {
    return null;
  }
}

export function writeSiteMapLayerVisibility(
  siteId: string,
  state: SavedSiteMapLayerVisibility,
): void {
  if (!siteId) return;
  try {
    rawSet(KEY_PREFIX + siteId, JSON.stringify({
      v: VERSION,
      hiddenCategories: state.hiddenCategories,
      hiddenLayerIds: state.hiddenLayerIds,
      hiddenParcelIds: state.hiddenParcelIds,
      ...(state.hiddenGeometryTypes && state.hiddenGeometryTypes.length > 0
        ? { hiddenGeometryTypes: state.hiddenGeometryTypes }
        : {}),
      ...(state.contextDatasets && state.contextDatasets.length > 0
        ? { contextDatasets: state.contextDatasets }
        : {}),
      ...(state.categoryOpacity && Object.keys(state.categoryOpacity).length > 0
        ? { categoryOpacity: state.categoryOpacity }
        : {}),
      ...(state.layerOrder && state.layerOrder.length > 0
        ? { layerOrder: state.layerOrder }
        : {}),
    }));
  } catch {
    /* losing view prefs is fine */
  }
}

/** Test hook — remove all saved layer-visibility entries (both backends). */
export function clearSiteMapLayerVisibilityForTests(): void {
  rawClearPrefix(KEY_PREFIX);
}
